import { autoScrolling } from '../../../../assets/js/application/index.js';

import { getProductArchiveFilterComponent } from './productArchiveFilter.vue.js';
import { getProductArchiveCounterComponent } from './productArchiveCounter.vue.js';
import { getProductArchiveFilterIndicatorComponent } from './productArchiveFilterIndicator.vue.js';
import { getProductArchivePaginationComponent } from './productArchivePagination.vue.js';

export function getProductArchiveComponent(idBase) {
    return {
        name: 'ProductArchive',
        components: {
            ProductArchiveFilter: getProductArchiveFilterComponent(idBase),
            ProductArchiveCounter: getProductArchiveCounterComponent(idBase),
            ProductArchiveFilterIndicator: getProductArchiveFilterIndicatorComponent(idBase),
            ProductArchivePagination: getProductArchivePaginationComponent(idBase),
        },
        props: {
            endpoint: {
                type: String,
                required: true,
            },
            initialProducts: {
                type: Array,
                required: true,
            },
            total: {
                type: Number,
                required: true,
            },
            perPage: {
                type: Number,
                required: true,
            },
            current: {
                type: Number,
                required: true,
            },
            ordering: {
                type: Object,
                required: true,
            },
            pagination: {
                type: Object,
                required: true,
            },
            filterAttributes: {
                type: Array,
                required: true,
            },
            filterCategories: {
                type: Object,
                required: true,
            },
            taxonomy: {
                type: Object,
                required: false,
                default: null,
            },
        },
        data() {
            return {
                paginationPages: this.pagination.pages,
                currentPage: this.current,
                url: new URL(this.endpoint),
                allProducts: this.initialProducts,
                isLoading: false,
                orderBy: this.ordering.value,
                hideBundles: false,
                showOnlyInStock: false,
                showOnlyForwardingGoods: false,
                allProductsFetched: false,
                filterBy: {},
            };
        },
        computed: {
            hasCategories() {
                return this.filterCategories.values.length > 0;
            },
            hasSubCategories() {
                return (
                    this.hasCategories && this.filterCategories.values.some((category) => category.children.length > 0)
                );
            },
            filterSubCategories() {
                let values = [];

                if (this.filterBy?.category?.length) {
                    values =
                        this.filterCategories.values.find((category) => category.value === this.filterBy.category[0])
                            .children ?? [];
                }

                return {
                    id: 'subcategory',
                    label: 'Unterkategorie',
                    name: 'subcategory',
                    values,
                };
            },
            hasSubSubCategories() {
                return (
                    this.hasSubCategories &&
                    this.filterCategories.values.some((category) =>
                        category.children.some((subCategory) => subCategory.children.length > 0),
                    )
                );
            },
            filterSubSubCategories() {
                let values = [];

                if (this.filterSubCategories.values.length) {
                    values =
                        this.filterSubCategories.values.find(
                            (category) => category.value === this.filterBy.subcategory[0],
                        )?.children ?? [];
                }

                return {
                    id: 'subsubcategory',
                    label: 'Subkategorie',
                    name: 'subsubcategory',
                    values,
                };
            },
            mergedFilterAttributes() {
                return [
                    this.filterCategories.values.length ? this.filterCategories : null,
                    this.hasSubCategories ? this.filterSubCategories : null,
                    this.hasSubSubCategories ? this.filterSubSubCategories : null,
                    ...this.filterAttributes,
                ].filter(Boolean);
            },
            hasCurrentPageChanged() {
                return this.currentPage !== this.current;
            },
            filteredProducts() {
                return [...this.allProducts]
                    .filter((product) => {
                        return !this.showOnlyInStock || (product.isInStock && !product.isForwardingGoods);
                    })
                    .filter((product) => {
                        return !this.showOnlyForwardingGoods || product.isForwardingGoods;
                    })
                    .filter((product) => {
                        return !this.hideBundles || !product.isBundle;
                    })
                    .filter((product) => {
                        let showProduct = true;

                        for (const filterAttribute in this.filterBy) {
                            if (Object.prototype.hasOwnProperty.call(this.filterBy, filterAttribute)) {
                                if (!this.filterBy[filterAttribute]?.length) {
                                    continue;
                                }

                                if (/^(?:sub)*category$/.test(filterAttribute)) {
                                    showProduct =
                                        'categories' in product &&
                                        product.categories.length &&
                                        this.filterBy[filterAttribute].some((category) =>
                                            product.categories.find((c) => c.slug === category),
                                        );
                                } else {
                                    showProduct =
                                        Object.prototype.hasOwnProperty.call(product.attributes, filterAttribute) &&
                                        this.filterBy[filterAttribute].some(
                                            (value) => value in product.attributes[filterAttribute],
                                        );
                                }

                                if (showProduct === false) {
                                    break;
                                }
                            }
                        }

                        return showProduct;
                    });
            },
            sortedProducts() {
                let orderFn;

                switch (this.orderBy) {
                    default:
                    case 'menu_order':
                        orderFn = this.orderDefault;
                        break;

                    case 'date':
                        orderFn = this.orderByDate;
                        break;

                    case 'price':
                        orderFn = this.orderByPrice;
                        break;
                    case 'price-desc':
                        orderFn = this.orderByPriceDESC;
                        break;
                }
                return [...this.filteredProducts].sort(orderFn);
            },

            products() {
                let firstIndex = this.perPage * (this.sanitizedCurrentPage - 1);

                if (!this.hasCurrentPageChanged && this.isLoading) {
                    firstIndex = 0;
                } else if (this.hasCurrentPageChanged && this.isLoading) {
                    return [];
                }

                return this.sortedProducts.slice(firstIndex, firstIndex + this.perPage);
            },
            totalProducts() {
                return this.products.length !== this.total && !this.isLoading ? this.products.length : this.total;
            },
            pages() {
                return this.allProductsFetched
                    ? this.paginationPages.slice(0, Math.ceil(this.filteredProducts.length / this.perPage))
                    : this.paginationPages;
            },
            sanitizedCurrentPage() {
                return Math.min(Math.max(1, this.currentPage), this.pages.length);
            },
        },
        async created() {
            if (this.pagination.pages.length > 1) {
                await this.fetchAllProducts();
            }
        },
        mounted() {
            window.addEventListener('popstate', () => {
                const url = new URL(location.href),
                    filterBy = { ...this.filterBy };

                let orderBy, currentPageIndex;

                if (url.searchParams.has('orderby')) {
                    orderBy = url.searchParams.get('orderby');
                }

                currentPageIndex = this.paginationPages.findIndex((p) => {
                    const pageLink = new URL(p.link);

                    return pageLink.hostname === url.hostname && pageLink.pathname === url.pathname;
                });

                if (orderBy in this.ordering.options) {
                    this.orderBy = orderBy;
                }

                this.filterBy = { ...filterBy };

                if (currentPageIndex !== -1) {
                    this.currentPage = currentPageIndex + 1;
                }
            });
        },
        methods: {
            updateUrl() {
                const url = new URL(this.pagination.pages[this.currentPage - 1].link),
                    pages = this.paginationPages;

                url.searchParams.set('orderby', this.orderBy);

                for (const page of pages) {
                    const link = new URL(page.link);

                    link.searchParams.set('orderby', this.orderBy);

                    page.link = link;
                }

                this.paginationPages = pages;

                history.pushState({}, null, url);
            },
            orderDefault(a, b) {
                if (this.hasCategories) {
                    if (a.categories.length && b.categories.length) {
                        const aCategoryOrders = a.categories.map((category) => Number(category.order)).sort(),
                            bCategoryOrders = b.categories.map((category) => Number(category.order)).sort(),
                            minLength = Math.min(aCategoryOrders.length, bCategoryOrders.length);

                        let aCategoryNames, bCategoryNames;

                        for (let i = 0; i < minLength; i++) {
                            if (aCategoryOrders[i] < bCategoryOrders[i]) {
                                return -1;
                            } else if (aCategoryOrders[i] > bCategoryOrders[i]) {
                                return 1;
                            }
                        }

                        aCategoryNames = a.categories.map((category) => category.name).sort();
                        bCategoryNames = b.categories.map((category) => category.name).sort();

                        for (let i = 0; i < minLength; i++) {
                            if (aCategoryNames[i] < bCategoryNames[i]) {
                                return -1;
                            } else if (aCategoryNames[i] > bCategoryNames[i]) {
                                return 1;
                            }
                        }
                    }
                }

                return a.title.localeCompare(b.title, navigator.language, { sensitivity: 'variant' });
            },
            orderByDate(a, b) {
                if (a.date > b.date) {
                    return -1;
                } else if (a.date < b.date) {
                    return 1;
                }

                return 0;
            },
            orderByPrice(a, b) {
                if (a.price < b.price) {
                    return -1;
                } else if (a.price > b.price) {
                    return 1;
                }

                return 0;
            },
            orderByPriceDESC(a, b) {
                if (a.price < b.price) {
                    return 1;
                } else if (a.price > b.price) {
                    return -1;
                }

                return 0;
            },
            onCurrentPageChanged(pageNo) {
                this.currentPage = pageNo;

                this.updateUrl();

                this.$nextTick(async () => {
                    (await autoScrolling()).scroll(this.$refs.indicatorBar);
                });
            },
            sanitizeCategoryFilters(models) {
                const hasCategory = Boolean(models.category?.length),
                    hasSubcategory = Boolean(models.subcategory?.length),
                    hasSubsubcategory = Boolean(models.subsubcategory?.length);

                if (!hasCategory) {
                    if (hasSubcategory) {
                        models.subcategory = [];
                    }

                    if (hasSubsubcategory) {
                        models.subsubcategory = [];
                    }
                } else if (!hasSubcategory && hasSubsubcategory) {
                    models.subsubcategory = [];
                }

                return models;
            },
            onFilterChanged(settings) {
                this.orderBy = settings.orderBy;
                this.hideBundles = settings.hideBundles;
                this.showOnlyInStock = settings.showOnlyInStock;
                this.showOnlyForwardingGoods = settings.showOnlyForwardingGoods;
                this.filterBy = this.sanitizeCategoryFilters({ ...settings.filterBy });

                this.updateUrl();
            },
            deleteFilters(filters) {
                const filterBy = {};

                for (const filter of filters) {
                    switch (filter.type) {
                        case 'checkbox':
                            this[filter.setting] = false;
                            break;

                        case 'checkboxes':
                            filterBy[filter.setting] = filterBy[filter.setting] ?? [...this.filterBy[filter.setting]];
                            filterBy[filter.setting].splice(filterBy[filter.setting].indexOf(filter.value), 1);
                            break;

                        case 'select':
                            filterBy[filter.setting] = '';
                            break;

                        default:
                            break;
                    }
                }

                this.filterBy = { ...this.filterBy, ...filterBy };
            },
            async fetchAllProducts() {
                this.isLoading = true;

                const url = new URL(this.url);

                url.pathname += 'all/';
                url.searchParams.set('orderby', this.ordering.value);
                url.searchParams.set('order', this.ordering.order);

                if (this.taxonomy) {
                    url.searchParams.set('tax', this.taxonomy.taxonomy);
                    url.searchParams.set('term-id', this.taxonomy.term_id);
                }

                try {
                    const response = await fetch(url.toString(), {
                            method: 'GET',
                            headers: {
                                'Content-type': 'application/json',
                                Accept: 'application/json',
                            },
                        }),
                        result = response.ok ? await response.json() : { success: false };

                    if (result.success) {
                        this.allProducts = result.data.products;
                        this.allProductsFetched = true;
                    } else {
                        throw '';
                    }
                } catch (e) {
                    //
                    console.error(e);
                } finally {
                    this.isLoading = false;
                }
            },
        },
        template: `#${idBase}-app`,
    };
}
